import { Invalid, Valid } from "#root/utility/validators";
import { useEffect, useRef } from "react";
import { useFieldSet } from "./fieldset";

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

const SITE_KEY = process.env.TURNSTILE_SITE_KEY || "1x00000000000000000000AA";

type TurnstileAPI = {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
      theme?: "light" | "dark" | "auto";
    },
  ) => string;
  remove: (widgetId: string) => void;
  reset: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("Failed to load Turnstile")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Turnstile"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export function TurnstileField() {
  const fieldSet = useFieldSet();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    fieldSet?.mutate((object) => ({
      ...object,
      dirty: false,
      value: null,
      validity: Invalid,
      hint: "Complete the check above",
    }));

    (async () => {
      try {
        await loadTurnstileScript();
        if (cancelled || !containerRef.current || !window.turnstile) {
          return;
        }
        if (widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token: string) => {
            fieldSet?.mutate((object) => ({
              ...object,
              dirty: true,
              value: token,
              validity: Valid,
              hint: "Verified",
            }));
          },
          "expired-callback": () => {
            fieldSet?.mutate((object) => ({
              ...object,
              dirty: true,
              value: null,
              validity: Invalid,
              hint: "Verification expired — try again",
            }));
          },
          "error-callback": () => {
            fieldSet?.mutate((object) => ({
              ...object,
              dirty: true,
              value: null,
              validity: Invalid,
              hint: "Verification failed — try again",
            }));
          },
        });
      } catch {
        fieldSet?.mutate((object) => ({
          ...object,
          dirty: true,
          value: null,
          validity: Invalid,
          hint: "Unable to load verification",
        }));
      }
    })();

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
    // fieldSet identity is stable enough for mount/unmount widget lifecycle
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} className="my-2" />;
}
